// The vendor exit, as the server describes it: the approval chain, the
// evidence behind each step, legal holds, the exit plan, proposals and the
// dossier.
//
// Its own file rather than more of api.ts, because every shape here belongs to
// one phase of the product and one screen, and a reviewer reading the exit
// should find its whole client in one place. Nothing here decides a rule. The
// chain, which stages are in force, who may sign and what each item accepts as
// evidence all arrive from the API, so the screen and the refusal cannot drift.

import { ApiError, request, requestFile, type OffboardingGate, type OffboardingItemState } from './api'

// ------------------------------------------------------------ the chain

export type VendorExitRecord = {
  id?: string
  vendorId: string
  status: 'PROPOSED' | 'OPEN' | 'CLOSED' | 'CALLED_OFF' | 'DISMISSED' | string
  // False for an exit that began before exits were recorded.
  recorded: boolean
  triggerKind: string
  triggerDetail?: Record<string, unknown>
  triggerKey?: string
  reason?: string
  proposedBy?: string
  proposedAt?: string
  openedBy?: string
  openedAt?: string
  closedBy?: string
  closedAt?: string
  endedBy?: string
  endedAt?: string
  endReason?: string
  notified?: { audience: string; name: string; status: string; detail: string }[]
  createdAt?: string
}

export type ExitStageState = {
  index: number
  key: string
  job: string
  roles: string[]
  roleNames: string[]
  when?: string
  why: string
  state: 'signed' | 'declined' | 'outstanding' | string
  signedBy?: string
  signedAt?: string
  signedRole?: string
  note?: string
  superseded?: string
  next: boolean
}

export type ExitFacts = {
  tier: number
  tierLabel: string
  tierBasis: string
  materialOutsourcing: boolean
  personalData: boolean
  personalDataBasis: string
  noAlternate: boolean
  alternateBasis: string
}

export type ExitChain = {
  vendorId: string
  vendorStatus: string
  exit: VendorExitRecord | null
  facts: ExitFacts
  ruleSource: string
  matrixVersionId?: string
  matrixVersionNo?: number
  chainSha256: string
  rowTier: number
  rowNoAlternate: boolean
  rowWhy: string
  notify: { code: string; name: string }[]
  stages: ExitStageState[]
  unsigned: ExitStageState[]
  signed: number
  total: number
  met: boolean
  maker?: string
  // Decisions standing on a stage the chain in force no longer asks for.
  other?: { id: string; stageKey: string; job: string; decision: string; signedBy: string; signedAt: string }[]
}

export type ExitView = {
  vendorId: string
  status: string
  exitApproval: ExitChain
  exits: VendorExitRecord[]
  message: string
  signable: boolean
  note?: string
  offboarding?: OffboardingGate
}

export type ExitDecisionResult = {
  decision: { stageKey: string; job: string; decision: string; signedBy: string; signedAt: string }
  exitApproval: ExitChain
  note: string
}

// The chain carried by a refused close, read the way the checklist is.
export function exitChainFromError(err: unknown): ExitChain | null {
  if (!(err instanceof ApiError)) return null
  const body = err.body as { exitApproval?: ExitChain } | null
  return body?.exitApproval ?? null
}

// ------------------------------------------------------------ evidence

export type EvidenceKind =
  | 'DOCUMENT'
  | 'REVOCATION'
  | 'NONE_HELD'
  | 'NOTICE'
  | 'RETURN'
  | 'DESTRUCTION'
  | 'RETENTION'
  | 'LEGAL_HOLD'

export type RevokedAccess = { system: string; identifier: string; revokedOn: string; revokedBy?: string }

export type OffboardingEvidence = {
  kind: EvidenceKind
  documentId?: string
  documentTitle?: string
  documentSha256?: string
  revoked?: RevokedAccess[]
  statement?: string
  confirmedBy?: string
  servedOn?: string
  servedTo?: string
  method?: string
  transitionCompletedOn?: string
  returned?: boolean
  receivedOn?: string
  what?: string
  deletedOn?: string
  performedBy?: string
  retainedUntil?: string
  protection?: string
  legalHoldId?: string
}

// The checklist item with what the evidence slice added. Declared here as an
// extension so the older screens reading OffboardingItemState keep compiling.
export type ExitItemState = OffboardingItemState & {
  recordedDone?: boolean
  evidence?: OffboardingEvidence
  evidenceSha256?: string
  accepts?: EvidenceKind[]
  evidenceMissing?: string
  // What the customer's directory showed during this exit, on the one item a
  // directory can prove (CREDENTIALS_DISABLED). Shown beside a person's record,
  // never in place of it. Absent when nothing was observed.
  observed?: DirectoryObservation
}

// An exit's proof from Microsoft Entra ID that the vendor's accounts are closed.
// met is true only when at least one account was found and every one is closed.
export type DirectoryObservation = {
  source: 'ENTRA_ID' | string
  total: number
  closed: number
  met: boolean
  observedAt: string
  accounts: ObservedAccount[]
}

export type ObservedAccount = {
  upn: string
  displayName?: string
  enabled: boolean
  // Disabled, or gone from the directory.
  closed: boolean
  disabledBy?: string
  disabledAt?: string
  removedAt?: string
  lastSignInAt?: string
  observedAt: string
}

// Where a vendor's accounts are looked for in the customer's directory.
export type DirectoryDomains = {
  vendorId: string
  domains: string[]
  groupId: string
  collecting: boolean
  note: string
}

// ------------------------------------------------------------ legal holds

export type LegalHold = {
  id: string
  vendorId: string
  reason: string
  reference?: string
  placedBy: string
  placedAt: string
  releasedBy?: string
  releasedAt?: string
  releaseReason?: string
  standing: boolean
}

// ------------------------------------------------------------ exit plan

export type ExitScenario = { kind: string; approach: string; targetDays: number }

export type ExitPlan = {
  triggers: string[]
  scenarios: ExitScenario[]
  alternateProvider?: string
  noAlternate: boolean
  alternateNote?: string
  lastTestedOn?: string
  testSummary?: string
}

export type StoredExitPlan = ExitPlan & {
  id: string
  contentSha256: string
  recordedBy: string
  recordedAt: string
  isCurrent: boolean
}

export type ExitPlanPosition = {
  vendorId: string
  material: boolean
  materialBasis: string
  standing: 'MISSING' | 'STALE' | 'CURRENT' | string
  detail: string
  plan?: StoredExitPlan
  inScope: boolean
}

export type ExitPlanView = {
  position: ExitPlanPosition
  revisions: StoredExitPlan[]
  vocabulary: {
    triggers: { code: string; name: string }[]
    scenarioKinds: { code: string; name: string }[]
    staleAfterMonths: number
  }
}

// ------------------------------------------------------------ the matrix section

// One stage of an exit row, as the approval matrix document stores it.
export type ExitStageDoc = { key: string; job: string; roles: string[]; when?: string; why: string }

// The exit chain for one tier, or for Tier 1 where the provider has no
// alternate.
export type ExitRowDoc = {
  tier: number
  noAlternate?: boolean
  stages: ExitStageDoc[]
  notify?: string[]
  why: string
}

// What the version list serves for the exit editor, so it holds no copy.
export type ExitVocabulary = {
  exitConditions: { code: string; name: string }[]
  exitAudiences: { code: string; name: string; reach: string }[]
  defaultExit: ExitRowDoc[]
}

// ------------------------------------------------------------ proposals

export type ExitProposal = VendorExitRecord & { legalName: string; vendorCode: string; vendorStatus: string }

// ------------------------------------------------------------ the board

// One exit as the Offboarding board reads it, from GET /v1/exits. The stages
// are the vendor page's chain (see exits_summary.go); the positions and the
// blocker are worked out there, so a card and the vendor page cannot disagree.
export type ExitSummaryStage = {
  key: string
  job: string
  state: 'signed' | 'declined' | 'outstanding' | string
  signedBy?: string
  signedAt?: string
  // The checklist items this stage vouches for.
  items: string[]
}

export type ExitSummary = {
  vendorId: string
  vendorName: string
  legalName: string
  vendorCode: string
  vendorStatus: string
  tier: number | null
  laneState: 'PROPOSED' | 'UNDER_WAY' | 'SIGNED_OUT'
  exitId?: string
  recorded: boolean
  triggerKind: string
  reason?: string
  proposedBy?: string
  proposedAt?: string
  openedBy?: string
  openedAt?: string
  closedBy?: string
  closedAt?: string
  stageCount: number
  // 0-based index of the first unsigned stage; equal to stageCount when all are signed.
  stageIndex: number
  stages: ExitSummaryStage[]
  currentJob?: string
  currentRoles: string[]
  // The plan's end date for an exit under way, absent without a plan.
  dueOn?: string
  plannedDays?: number
  blocker?: string
  blockerItemKey?: string
  daysOpen?: number
}

// ------------------------------------------------------------ calls

const v = (vendorId: string) => '/v1/vendors/' + encodeURIComponent(vendorId)

export const exitApi = {
  vendorExit: (vendorId: string) => request<ExitView>(v(vendorId) + '/exit'),

  decide: (vendorId: string, body: { stageKey: string; decision: 'SIGN' | 'DECLINE'; note: string }) =>
    request<ExitDecisionResult>(v(vendorId) + '/exit/decisions', { method: 'POST', body: JSON.stringify(body) }),

  recordItem: (
    vendorId: string,
    key: string,
    body: { done: boolean; note: string; evidence?: OffboardingEvidence },
  ) =>
    request<{ item: ExitItemState; changed: boolean; offboarding: OffboardingGate; message: string }>(
      v(vendorId) + '/offboarding/' + encodeURIComponent(key),
      { method: 'PUT', body: JSON.stringify(body) },
    ),

  legalHolds: (vendorId: string) => request<{ holds: LegalHold[]; standing: number }>(v(vendorId) + '/legal-holds'),

  placeLegalHold: (vendorId: string, body: { reason: string; reference: string }) =>
    request<{ hold: LegalHold; note: string }>(v(vendorId) + '/legal-holds', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  releaseLegalHold: (vendorId: string, holdId: string, reason: string) =>
    request<{ hold: LegalHold; note: string }>(
      v(vendorId) + '/legal-holds/' + encodeURIComponent(holdId) + '/release',
      { method: 'POST', body: JSON.stringify({ reason }) },
    ),

  exitPlan: (vendorId: string) => request<ExitPlanView>(v(vendorId) + '/exit-plan'),

  saveExitPlan: (vendorId: string, plan: ExitPlan) =>
    request<{ plan: StoredExitPlan; position: ExitPlanPosition; note: string }>(v(vendorId) + '/exit-plan', {
      method: 'PUT',
      body: JSON.stringify(plan),
    }),

  directoryDomains: (vendorId: string) => request<DirectoryDomains>(v(vendorId) + '/directory-domains'),

  saveDirectoryDomains: (vendorId: string, body: { domains: string[]; groupId: string }) =>
    request<DirectoryDomains>(v(vendorId) + '/directory-domains', { method: 'PUT', body: JSON.stringify(body) }),

  proposals: () => request<{ proposals: ExitProposal[] }>('/v1/exit-proposals'),

  // Every exit's lane, stage and blocker in one read; vendorId narrows it to one.
  summary: (vendorId?: string) =>
    request<{ exits: ExitSummary[] }>('/v1/exits' + (vendorId ? '?vendorId=' + encodeURIComponent(vendorId) : '')),

  propose: (
    vendorId: string,
    body: { triggerKind: 'REGULATOR_INSTRUCTION' | 'MANUAL'; reason: string; regulator?: string; reference?: string; receivedOn?: string },
  ) =>
    request<{ proposal: VendorExitRecord; note: string }>(v(vendorId) + '/exit-proposals', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  confirm: (proposalId: string, reason: string) =>
    request<{ status: string; exitId?: string }>('/v1/exit-proposals/' + encodeURIComponent(proposalId) + '/confirm', {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),

  dismiss: (proposalId: string, reason: string) =>
    request<{ id: string; status: string; note: string }>(
      '/v1/exit-proposals/' + encodeURIComponent(proposalId) + '/dismiss',
      { method: 'POST', body: JSON.stringify({ reason }) },
    ),

  // The pack as a file to keep, in either form. Both carry the bearer token,
  // which a plain link could not.
  dossierFile: async (vendorId: string, format: 'json' | 'html') => {
    const file = await requestFile(v(vendorId) + '/exit/dossier' + (format === 'html' ? '?format=html' : ''))
    const stem = 'exit-dossier-' + vendorId.slice(0, 8)
    return { blob: file.blob, filename: stem + (format === 'html' ? '.html' : '.json') }
  },
}
